<template>
  <div class="view users-page" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('user_management')"
      :grid-ref="$refs.usersGrid"
      :show-add="canEdit"
      :show-export="true"
      :show-columns="true"
      :show-refresh="true"
      @add="newObject"
      @export="exportGrid"
    />
    <div class="users-grid-container">
      <DxDataGrid
        class="users-grid"
        :element-attr="gridAttributes"
        ref="usersGrid"
        :data-source="usersDataSource"
        key-expr="uuid"
        :remote-operations="remoteOperations"
        :show-borders="true"
        :row-alternation-enabled="true"
        :hover-state-enabled="true"
        :column-auto-width="true"
        :allow-column-resizing="true"
        :column-hiding-enabled="true"
      >
      <DxColumn data-field="name" data-type="string" :caption="$t('common.name')" />
      <DxColumn data-field="email" data-type="string" :caption="$t('common.e_mail')" />
      <DxColumn
        data-field="units"
        :caption="$t('user.units_and_roles')"
        cell-template="unitsAndRolesCell"
        :allow-sorting="false"
        :allow-filtering="false"
        :allow-exporting="false"
      />
      <DxColumn
        data-field="is_active"
        data-type="boolean"
        :caption="$t('common.status')"
        :true-text="$t('common.active')"
        :false-text="$t('common.inactive')"
        cell-template="statusCell"
      />
      <DxColumn
        data-field="api_access"
        data-type="boolean"
        :caption="$t('user.api_access')"
        :true-text="$t('common.yes')"
        :false-text="$t('common.no')"
        cell-template="apiAccessCell"
      />
      <DxColumn
        data-field="created_at"
        data-type="date"
        :format="getDxGridDefaultDateFormat || 'dd-MM-yyyy'"
        :caption="$t('common.created_at')"
        :allow-filtering="false"
      />
      <DxColumn
        data-field="updated_at"
        data-type="date"
        :format="getDxGridDefaultDateFormat || 'dd-MM-yyyy'"
        :caption="$t('common.last_update')"
        :allow-filtering="false"
      />
      <DxColumn
        :width="60"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #unitsAndRolesCell="{ data }">
        <UnitAndRoleLink
          v-if="hasUnitsRolesOrTypes(data.data)"
          :data="data.data"
          @open-dialog="openUnitsAndRoleDialog"
        />
        <span v-else>-</span>
      </template>
      <template #statusCell="{ data }">
        <span>{{ data.data.is_active ? $t('common.active') : $t('common.inactive') }}</span>
      </template>
      <template #apiAccessCell="{ data }">
        <span>{{ data.data.api_access ? $t('common.yes') : $t('common.no') }}</span>
      </template>
      <template #actionsCell="{ data }">
        <DxActionMenu
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :edit-action="editUser"
          :delete-action="deleteUser"
          :items="getActionItems()"
        />
      </template>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[15, 50, 100]"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxFilterRow :visible="true" :show-operation-chooser="false" />
      <DxColumnChooser :enabled="false" mode="select" />
      </DxDataGrid>
    </div>
    <UnitsAndRoleDialog
      :show="unitsAndRoleDialogOpen"
      :data="unitsAndRoleDialogData"
      @close="unitsAndRoleDialogOpen = false"
    />
    <ChangePasswordDialog
      :show="showPasswordModal"
      :data="passwordModalData"
      @close="showPasswordModal = false"
      @onPasswordChanged="onPasswordChanged"
    />
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DxDataGrid, { DxColumn, DxColumnChooser, DxFilterRow, DxPager, DxPaging } from "devextreme-vue/data-grid";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import { canDelete, canEdit } from "@/helpers/Permissions";
import { errorMessageList, successMessage } from "@/helpers/common";
import ChangePasswordDialog from "~/pages/users/partials/ChangePasswordDialog.vue";
import UnitAndRoleLink from "~/pages/users/partials/UnitAndRoleLink.vue";
import UnitsAndRoleDialog from "~/pages/users/partials/UnitsAndRoleDialog.vue";
import Forbidden from "~/pages/errors/Forbidden.vue";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";

export default {
  name: 'UsersPage',
  middleware: ['auth', 'check-company-admin'],
  data() {
    return {
      filters: { page: 1 },
      showPasswordModal: false,
      passwordModalData: {},
      unitsAndRoleDialogOpen: false,
      unitsAndRoleDialogData: {},
      exportLimitOverride: null,
      usersDataSource: null,
      remoteOperations: { paging: true, filtering: true },
      gridAttributes: {
        class: "smagria-grid users-grid",
      },
    }
  },
  components: {
    Forbidden,
    DxDataGrid,
    DxColumn,
    DxColumnChooser,
    DxFilterRow,
    DxPager,
    DxPaging,
    DxActionMenu,
    GridPanelHeader,
    ChangePasswordDialog,
    UnitAndRoleLink,
    UnitsAndRoleDialog,
  },
  created() {
    this.usersDataSource = new DataSource({
      store: new CustomStore({
        key: "uuid",
        load: this.loadUsers,
      }),
    });
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      getDxGridDefaultDateFormat: 'settings/getDxGridDefaultDateFormat',
    }),
    usersDialogOpen() {
      return this.unitsAndRoleDialogOpen || this.showPasswordModal;
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      deleteObject: 'user/deleteUser',
      getUsers: 'user/getUsers',
      changePassword: 'user/changePassword',
    }),
    loadUsers(loadOptions) {
      const pageSize = loadOptions.take || this.exportLimitOverride || 15;
      const page = Math.floor((loadOptions.skip || 0) / pageSize) + 1;
      const params = {
        ...this.filters,
        page,
        limit: pageSize,
      };
      if (loadOptions.filter) {
        params.filter = JSON.stringify(loadOptions.filter);
      }

      return this.getUsers(params).then((response) => {
        const users = response && response.data && Array.isArray(response.data.data) ? response.data.data : [];
        const totalCount = response && response.data && response.data.meta ? response.data.meta.total : users.length;

        return {
          data: users.map((user) => ({
            ...user,
            is_active: Number(user.is_active) === 1,
            api_access: Number(user.api_access) === 1,
          })),
          totalCount,
        };
      });
    },
    newObject() {
      this.$router.push({
        path: '/users/add'
      });
    },
    editUser(user) {
      this.$router.push({ path: `/users/${user.uuid}` });
    },
    deleteUser(user) {
      this.$dialog.confirm(this.$t('common.confirm_delete', { title: user.name }), {}).then((dialog) => {
        this.deleteObject({ uuid: user.uuid }).then((response) => {
          successMessage(this, response.message);
          dialog.close();
          this.$refs.usersGrid.instance.refresh();
        });
      });
    },
    getActionItems() {
      if (!this.canEdit) {
        return [];
      }

      return [{
        key: 'change_password',
        label: this.$t('change_password'),
        text: this.$t('change_password'),
        action: this.showPasswordDialog,
      }];
    },
    showPasswordDialog(user) {
      this.passwordModalData = { ...user, password: '', confirm_password: '' };
      this.showPasswordModal = true;
    },
    openUnitsAndRoleDialog(user) {
      this.unitsAndRoleDialogData = user;
      this.unitsAndRoleDialogOpen = true;
    },
    onPasswordChanged(data) {
      this.changePassword(data).then((response) => {
        successMessage(this, response.message);
      }).catch((error) => {
        errorMessageList(this, error.errors);
      });
    },
    hasUnitsRolesOrTypes(user) {
      const units = Array.isArray(user.units) ? user.units : [];
      return this.getCount(user.units) > 0
        || this.getCount(user.roles) > 0
        || Number(user.is_superadmin) === 1
        || Number(user.is_company_admin) === 1
        || units.some((unit) => Number(unit.is_department_owner) === 1 || Number(unit.is_department_admin) === 1);
    },
    getCount(value) {
      if (Array.isArray(value)) {
        return value.length;
      }
      return Number(value) || 0;
    },
    async exportGrid() {
      const instance = this.$refs.usersGrid && this.$refs.usersGrid.instance;
      if (!instance) return;

      const dataSource = instance.getDataSource();
      const originalPageSize = dataSource.pageSize();
      const originalPageIndex = dataSource.pageIndex();
      const currentFilter = dataSource.filter();

      try {
        const totalCount = await dataSource.totalCount();
        if (Number.isFinite(totalCount) && totalCount > 0) {
          this.exportLimitOverride = totalCount;
          dataSource.pageIndex(0);
          dataSource.pageSize(totalCount);
          await dataSource.load();
        }

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(this.$t('common.users'));
        await exportDataGrid({
          component: instance,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (!gridCell || gridCell.rowType !== 'data') return;

            if (gridCell.column.dataField === 'is_active') {
              excelCell.value = gridCell.data.is_active ? this.$t('common.active') : this.$t('common.inactive');
            } else if (['created_at', 'updated_at'].includes(gridCell.column.dataField) && gridCell.value) {
              const date = new Date(gridCell.value);
              if (!Number.isNaN(date.getTime())) {
                excelCell.value = format(date, 'dd-MM-yyyy');
              }
            }
          },
        });
        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `Users_${format(new Date(), 'yyyy_MM_dd_HH_mm_ss')}.xlsx`;
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
      } finally {
        this.exportLimitOverride = null;
        dataSource.filter(currentFilter);
        dataSource.pageSize(originalPageSize);
        dataSource.pageIndex(originalPageIndex);
        await dataSource.load();
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";

.users-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.users-grid-container {
  flex: 1 1 0;
  min-height: 0;
}

.users-grid {
  height: 100%;
}
</style>
